"use client";
/**
 * useSessions — manages the conversation sidebar list.
 * Track C2.
 */
import { useState, useCallback, useEffect } from "react";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { isPaidProfile } from "@/shared/types";
import {
  getSessions,
  updateSession,
  deleteSession,
} from "@/platform/firebase/firestore/ask";
import type { Session } from "@/modes/ask/types";

interface UseSessionsReturn {
  sessions:      Session[];
  isLoading:     boolean;
  reload:        () => Promise<void>;
  toggleStar:    (sessionId: string) => Promise<void>;
  toggleArchive: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, newTitle: string) => Promise<void>;
  removeSession: (sessionId: string) => Promise<void>;
}

export function useSessions(): UseSessionsReturn {
  const { user, profile } = useAuthContext();

  const isPro = isPaidProfile(profile?.subscription);

  const [sessions,  setSessions]  = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── reload ──────────────────────────────────────────────────────────────

  const reload = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const data = await getSessions(user.uid);
      setSessions(data);
    } catch (err) {
      // Non-fatal — keep the previous list; never crash the page here.
      console.warn("[ask] load sessions failed", err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load on mount / user change
  useEffect(() => {
    reload();
  }, [reload]);

  // ── toggleStar ───────────────────────────────────────────────────────────

  const toggleStar = useCallback(
    async (sessionId: string) => {
      const target = sessions.find((s) => s.id === sessionId);
      if (!target) return;

      // Optimistic
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, isStarred: !s.isStarred } : s))
      );

      try {
        await updateSession(sessionId, { isStarred: !target.isStarred });
      } catch {
        await reload();
      }
    },
    [sessions, reload]
  );

  // ── toggleArchive ────────────────────────────────────────────────────────

  const toggleArchive = useCallback(
    async (sessionId: string) => {
      const target = sessions.find((s) => s.id === sessionId);
      if (!target) return;

      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, isArchived: !s.isArchived } : s))
      );

      try {
        await updateSession(sessionId, { isArchived: !target.isArchived });
      } catch {
        await reload();
      }
    },
    [sessions, reload]
  );

  // ── renameSession ────────────────────────────────────────────────────────

  const renameSession = useCallback(
    async (sessionId: string, newTitle: string) => {
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, title: newTitle } : s))
      );

      try {
        await updateSession(sessionId, { title: newTitle });
      } catch {
        await reload();
      }
    },
    [reload]
  );

  // ── removeSession ────────────────────────────────────────────────────────

  const removeSession = useCallback(
    async (sessionId: string) => {
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      await deleteSession(sessionId, isPro);
    },
    [isPro]
  );

  return {
    sessions,
    isLoading,
    reload,
    toggleStar,
    toggleArchive,
    renameSession,
    removeSession,
  };
}
